/**
 * mmWave presence radar — pure view logic. Server twin: backend/mmwave_sensor.py
 * (public_state). Design: docs/superpowers/specs/2026-09-17-mmwave-presence-sensor-design.md.
 *
 * Offline means presence is UNKNOWN, never "Clear" — the same rule the server applies.
 */
import type { Feature, Polygon } from 'geojson';

export const MMWAVE_FOV_DEG = 80;
const DEFAULT_RANGE_M = 6;

/** The `mmwave_state` WebSocket message and the GET body (minus zones). */
export interface MmwaveState {
  sensor_id: number;
  group_id: number | null;
  online: boolean;
  presence: boolean | null;
  targets: number | null;
  distance_cm: number | null;
  last_seen: string | null;
  presence_since: string | null;
  bearing_deg: number | null;
  range_m: number;
  has_token: boolean;
}

export interface MmwaveView {
  online: boolean;
  presence: boolean | null;
  targets: number | null;
  distanceCm: number | null;
  lastSeen: Date | null;
  presenceSince: Date | null;
  bearingDeg: number | null;
  rangeM: number;
}

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const date = (v: unknown): Date | null => {
  if (typeof v !== 'string' || !v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

export function mmwaveView(status: string, metadata: Record<string, unknown> | undefined): MmwaveView {
  const m = metadata ?? {};
  const online = status === 'active';
  return {
    online,
    presence: online && typeof m.presence === 'boolean' ? m.presence : null,
    targets: online ? num(m.targets) : null,
    distanceCm: online ? num(m.distance_cm) : null,
    lastSeen: date(m.last_seen),
    presenceSince: date(m.presence_since),
    bearingDeg: num(m.bearing_deg),
    rangeM: num(m.range_m) ?? DEFAULT_RANGE_M,
  };
}

export function presenceLabel(p: boolean | null): 'Occupied' | 'Clear' | 'Unknown' {
  if (p === null) return 'Unknown';
  return p ? 'Occupied' : 'Clear';
}

export function agoText(then: Date | null, nowMs: number): string {
  if (!then) return 'never';
  const s = Math.floor((nowMs - then.getTime()) / 1000);
  if (s < 1) return 'just now';
  if (s < 60) return `${s} s ago`;
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  return `${Math.floor(s / 3600)} h ago`;
}

// Destination point from lng/lat, bearing (deg) and distance (m). Same math as
// components/hall/HallOverlay.tsx dest().
function dest(lng: number, lat: number, brgDeg: number, distM: number): [number, number] {
  const R = 6378137, d = distM / R, b = (brgDeg * Math.PI) / 180;
  const la = (lat * Math.PI) / 180, lo = (lng * Math.PI) / 180;
  const la2 = Math.asin(Math.sin(la) * Math.cos(d) + Math.cos(la) * Math.sin(d) * Math.cos(b));
  const lo2 = lo + Math.atan2(Math.sin(b) * Math.sin(d) * Math.cos(la), Math.cos(d) - Math.sin(la) * Math.sin(la2));
  return [(lo2 * 180) / Math.PI, (la2 * 180) / Math.PI];
}

/** The radar's coverage wedge: sensor, arc of `steps + 1` points, back to the sensor. */
export function sectorPolygon(
  lat: number, lon: number, bearingDeg: number, rangeM: number,
  fovDeg: number = MMWAVE_FOV_DEG, steps: number = 16,
): Feature<Polygon> {
  const start = bearingDeg - fovDeg / 2;
  const ring: [number, number][] = [[lon, lat]];
  for (let i = 0; i <= steps; i++) ring.push(dest(lon, lat, start + (fovDeg * i) / steps, rangeM));
  ring.push([lon, lat]);
  return { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [ring] } };
}

/**
 * Merge a `mmwave_state` message into a sensor row, the way the server stores it.
 * I4: a sensor an operator put in maintenance keeps that status — a push can never
 * flip it back to active/inactive, though its metadata (last_seen etc.) still merges.
 */
export function applyMmwaveState<T extends { status: string; metadata?: Record<string, unknown> }>(
  sensor: T, msg: MmwaveState,
): T {
  return {
    ...sensor,
    status: (sensor.status === 'maintenance' ? 'maintenance' : msg.online ? 'active' : 'inactive') as T['status'],
    metadata: {
      ...(sensor.metadata ?? {}),
      presence: msg.presence,
      targets: msg.targets,
      distance_cm: msg.distance_cm,
      last_seen: msg.last_seen,
      presence_since: msg.presence_since,
      bearing_deg: msg.bearing_deg,
      range_m: msg.range_m,
    },
  };
}
